import React, { useContext, useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import StarekContext from "../../context/StarekContext";

/**
 * Operation-wise machine utilisation and bottleneck summary.
 *
 * Reads scheduled route steps rather than raw telemetry: every step records the machine
 * it ran on, the duration planned for it, and — once driven — when it started and
 * finished. That is enough to rank operations by how far actual time runs over plan,
 * and to show which machines absorb each operation's load.
 */
const GET_SCHEDULED_OPERATIONS = gql`
    query GetScheduledOperationsForPlant($plantId: Int!) {
        jobScheduleOperations(
            where: { job_schedule: { is: { plant_id: { equals: $plantId } } } }
        ) {
            id
            sequence_no
            status
            planned_duration
            start_time
            end_time
            device_id
            operation {
                id
                name
                is_productive
            }
            job_schedule {
                id
                name
                start_time
                end_time
                part {
                    id
                    name
                }
            }
        }
    }
`;

const STATUS_COMPLETED = 2;

const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const s = Math.round(seconds);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ${s % 60}s`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const formatVariance = (seconds) => {
    if (seconds == null) return "—";
    const sign = seconds > 0 ? "+" : "";
    return `${sign}${formatDuration(Math.abs(seconds))}`.replace("+—", "—");
};

function OperationReport({
    filters,
    range,
    onRefresh,
    onFavoriteClick,
    onDownloadCSV,
}) {
    const starekContext = useContext(StarekContext);
    const plantId = starekContext.lastUsedPlant?.id;

    const { data, loading, refetch } = useQuery(GET_SCHEDULED_OPERATIONS, {
        client: starekContext.engineClient,
        skip: !plantId,
        fetchPolicy: "network-only",
        variables: { plantId },
    });

    // The window comes from the shared report filters. Steps are matched on their
    // parent run's window, because a step only carries timestamps once it has run.
    const windowBounds = useMemo(() => {
        if (!filters?.durationStart || !range) return null;
        const base = new Date(filters.durationStart).getTime();
        return [base + range[0] * 1000, base + range[1] * 1000];
    }, [filters?.durationStart, range]);

    const selectedDeviceIds = useMemo(
        () =>
            filters?.devices
                ? filters.devices.split(",").filter((id) => id && !id.startsWith("shift_"))
                : [],
        [filters?.devices],
    );

    const selectedOperationIds = useMemo(
        () =>
            filters?.operations
                ? filters.operations
                      .split(",")
                      .filter(Boolean)
                      .map(Number)
                : [],
        [filters?.operations],
    );

    const rows = useMemo(() => {
        const steps = data?.jobScheduleOperations || [];

        const inWindow = (step) => {
            if (!windowBounds) return true;
            const start = new Date(step.job_schedule?.start_time).getTime();
            const end = new Date(step.job_schedule?.end_time).getTime();
            return start <= windowBounds[1] && end >= windowBounds[0];
        };

        const relevant = steps.filter((step) => {
            if (!inWindow(step)) return false;
            if (
                selectedDeviceIds.length &&
                !selectedDeviceIds.includes(step.device_id)
            ) {
                return false;
            }
            if (
                selectedOperationIds.length &&
                !selectedOperationIds.includes(Number(step.operation?.id))
            ) {
                return false;
            }
            return true;
        });

        const byOperation = new Map();

        relevant.forEach((step) => {
            const op = step.operation;
            if (!op) return;
            if (!byOperation.has(op.id)) {
                byOperation.set(op.id, {
                    id: op.id,
                    name: op.name,
                    isProductive: op.is_productive,
                    runs: 0,
                    completed: 0,
                    plannedTotal: 0,
                    actualTotal: 0,
                    measuredRuns: 0,
                    machines: new Map(),
                    parts: new Set(),
                });
            }
            const entry = byOperation.get(op.id);
            entry.runs += 1;
            if (step.status === STATUS_COMPLETED) entry.completed += 1;
            entry.plannedTotal += step.planned_duration || 0;
            if (step.job_schedule?.part?.name) {
                entry.parts.add(step.job_schedule.part.name);
            }

            // Actual time is only known for steps that both started and finished.
            let actual = null;
            if (step.start_time && step.end_time) {
                actual =
                    (new Date(step.end_time).getTime() -
                        new Date(step.start_time).getTime()) /
                    1000;
                if (actual >= 0) {
                    entry.actualTotal += actual;
                    entry.measuredRuns += 1;
                }
            }

            if (step.device_id) {
                const machine = entry.machines.get(step.device_id) || {
                    id: step.device_id,
                    runs: 0,
                    actual: 0,
                    planned: 0,
                };
                machine.runs += 1;
                machine.planned += step.planned_duration || 0;
                if (actual != null && actual >= 0) machine.actual += actual;
                entry.machines.set(step.device_id, machine);
            }
        });

        return [...byOperation.values()]
            .map((entry) => {
                // Compare like with like: only steps with a measured actual can be
                // set against plan, so scale the planned figure to those steps.
                const plannedForMeasured =
                    entry.runs > 0
                        ? (entry.plannedTotal / entry.runs) * entry.measuredRuns
                        : 0;
                const variance =
                    entry.measuredRuns > 0
                        ? entry.actualTotal - plannedForMeasured
                        : null;
                return {
                    ...entry,
                    machineList: [...entry.machines.values()].sort(
                        (a, b) => b.actual - a.actual,
                    ),
                    partList: [...entry.parts],
                    variance,
                    variancePct:
                        variance != null && plannedForMeasured > 0
                            ? (variance / plannedForMeasured) * 100
                            : null,
                };
            })
            .sort((a, b) => {
                // Bottlenecks first: worst overrun, then most time spent.
                const av = a.variance ?? -Infinity;
                const bv = b.variance ?? -Infinity;
                if (av !== bv) return bv - av;
                return b.actualTotal - a.actualTotal;
            });
    }, [data?.jobScheduleOperations, windowBounds, selectedDeviceIds, selectedOperationIds]);

    const totalActual = rows.reduce((sum, r) => sum + r.actualTotal, 0);
    const bottleneck = rows.find((r) => r.variance != null && r.variance > 0);

    const handleRefresh = () => {
        refetch();
        onRefresh?.();
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-items-center tw-justify-between">
                <h2 className="tw-text-sm tw-font-medium tw-text-slate-800">
                    Operation utilisation &amp; bottlenecks
                </h2>
                <div className="tw-flex tw-gap-2">
                    <button
                        type="button"
                        onClick={handleRefresh}
                        className="tw-rounded-md tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-xs hover:tw-bg-slate-50"
                    >
                        Refresh
                    </button>
                    {onFavoriteClick && (
                        <button
                            type="button"
                            onClick={onFavoriteClick}
                            className="tw-rounded-md tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-xs hover:tw-bg-slate-50"
                        >
                            Save as favourite
                        </button>
                    )}
                    {onDownloadCSV && (
                        <button
                            type="button"
                            onClick={() => onDownloadCSV(rows)}
                            className="tw-rounded-md tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-xs hover:tw-bg-slate-50"
                        >
                            CSV
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="tw-text-sm tw-text-slate-500 tw-p-4">
                    Loading operation data...
                </div>
            ) : rows.length === 0 ? (
                <div className="tw-text-sm tw-text-slate-500 tw-p-4">
                    No scheduled operations in this window. Schedule a job
                    against a part with a route to populate this report.
                </div>
            ) : (
                <>
                    {bottleneck && (
                        <div className="tw-rounded-md tw-border tw-border-amber-300 tw-bg-amber-50 tw-p-3 tw-text-sm">
                            <strong>Bottleneck:</strong> {bottleneck.name} is
                            running {formatVariance(bottleneck.variance)} over
                            plan
                            {bottleneck.variancePct != null &&
                                ` (${bottleneck.variancePct.toFixed(0)}%)`}{" "}
                            across {bottleneck.measuredRuns} measured run
                            {bottleneck.measuredRuns === 1 ? "" : "s"}.
                        </div>
                    )}

                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-sm">
                            <thead>
                                <tr className="tw-text-left tw-border-b tw-border-slate-200 tw-text-xs tw-text-slate-500">
                                    <th className="tw-py-2">Operation</th>
                                    <th className="tw-py-2">Runs</th>
                                    <th className="tw-py-2">Completed</th>
                                    <th className="tw-py-2">Planned</th>
                                    <th className="tw-py-2">Actual</th>
                                    <th className="tw-py-2">Variance</th>
                                    <th className="tw-py-2">Machines</th>
                                    <th className="tw-py-2">Share of time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => {
                                    const share =
                                        totalActual > 0
                                            ? (row.actualTotal / totalActual) *
                                              100
                                            : 0;
                                    return (
                                        <tr
                                            key={row.id}
                                            className="tw-border-b tw-border-slate-100 tw-align-top"
                                        >
                                            <td className="tw-py-2">
                                                <div className="tw-font-medium">
                                                    {row.name}
                                                </div>
                                                <div className="tw-text-xs tw-text-slate-500">
                                                    {row.isProductive
                                                        ? "Productive"
                                                        : "Non-productive"}
                                                    {row.partList.length > 0 &&
                                                        ` · ${row.partList.join(", ")}`}
                                                </div>
                                            </td>
                                            <td className="tw-py-2">
                                                {row.runs}
                                            </td>
                                            <td className="tw-py-2">
                                                {row.completed} of {row.runs}
                                            </td>
                                            <td className="tw-py-2">
                                                {formatDuration(
                                                    row.plannedTotal,
                                                )}
                                            </td>
                                            <td className="tw-py-2">
                                                {row.measuredRuns > 0
                                                    ? formatDuration(
                                                          row.actualTotal,
                                                      )
                                                    : "not run yet"}
                                            </td>
                                            <td
                                                className={`tw-py-2 ${
                                                    row.variance == null
                                                        ? "tw-text-slate-400"
                                                        : row.variance > 0
                                                          ? "tw-text-red-600"
                                                          : "tw-text-green-700"
                                                }`}
                                            >
                                                {formatVariance(row.variance)}
                                            </td>
                                            <td className="tw-py-2">
                                                {row.machineList.length === 0
                                                    ? "—"
                                                    : row.machineList.map(
                                                          (machine) => (
                                                              <div
                                                                  key={
                                                                      machine.id
                                                                  }
                                                                  className="tw-text-xs"
                                                              >
                                                                  {machine.id} ·{" "}
                                                                  {machine.runs}{" "}
                                                                  run
                                                                  {machine.runs ===
                                                                  1
                                                                      ? ""
                                                                      : "s"}
                                                                  {machine.actual >
                                                                      0 &&
                                                                      ` · ${formatDuration(machine.actual)}`}
                                                              </div>
                                                          ),
                                                      )}
                                            </td>
                                            <td className="tw-py-2">
                                                {row.measuredRuns > 0
                                                    ? `${share.toFixed(0)}%`
                                                    : "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="tw-text-xs tw-text-slate-500">
                        Actual time is measured only for steps that have both
                        started and finished, so variance covers those runs
                        alone. Planned time is scaled to the same steps for the
                        comparison.
                    </div>
                </>
            )}
        </div>
    );
}

export default OperationReport;
